// Imports
import { useRef, useEffect, useState, useCallback } from "react";
import Plotly from "plotly.js-dist";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, IconButton, Modal, Typography, Grid } from "@mui/material";
import { Edit, Check, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { metricTypes, metricNames } from "./metrics";
import { getZoomIds } from "../../../../api";

export const GraphComponent = ({ data, setData, setZoomLoading, annotations, dataset, customTitle, isEditingTitle, setIsEditingTitle, setCustomTitle, clusterNames, setClusterNames, clusterModalOpen, setClusterModalOpen }) => {
    // UseState definitions
    const [foundData, setFoundData] = useState(false);
    const [labelMode, setLabelMode] = useState("dynamic"); // "none", "all", "dynamic"
    const [plotInitialized, setPlotInitialized] = useState(false);

    const isOverlappingScatter = (x1, y1, x2, y2, rangeX, rangeY) => {
        // Adjust fraction to change how many labels to hide
        const fraction = 0.007;
        const xThreshold = rangeX * fraction;
        const yThreshold = rangeY * fraction;
        const xDistance = Math.abs(x1 - x2);
        const yDistance = Math.abs(y1 - y2);
        return xDistance < xThreshold && yDistance < yThreshold;
    };

    // Helper function to check if current metric supports dynamic labels
    const supportsDynamicLabels = () => {
        return metricTypes.scatter.includes(data.metric);
    };

    // Helper function to get available label modes based on graph type
    const getAvailableLabelModes = () => {
        const baseModes = [
            { value: "none", label: "No Labels" },
            { value: "all", label: "All Labels" }
        ];
        
        if (supportsDynamicLabels()) {
            baseModes.push({ value: "dynamic", label: "Dynamic Labels" });
        }
        
        return baseModes;
    };

    // Helper function to get default label mode based on graph type
    const getDefaultLabelMode = () => {
        if (supportsDynamicLabels()) {
            return "dynamic";
        }
        return "all"; // Default to showing all labels for non-scatter plots
    };

    const [layout, setLayout] = useState({
        title: "",  // Hide Plotly title since we have custom editable title
        width: 1000,
        height: 500,
        margin: {
            l: 50,
            r: 50,
            b: 50,
            t: 20,
            pad: 4
        },
        xaxis: {
            automargin: true,
            categoryorder: 'category ascending',
            title: {
                text: data.xLabel,
                standoff: 20
            }
        },
        yaxis: {
            automargin: true,
            categoryorder: 'category descending',
            tickangle: 0,
            title: {
                text: data.yLabel,
                standoff: 40
            }
        }
    });

    const navigate = useNavigate();
    const graph = useRef(null);

    // Function to choose which labels to show in a scatter plot
    const chooseLabels = (traces, xRange, yRange) => {
        const rangeX = xRange[1] - xRange[0];
        const rangeY = yRange[1] - yRange[0];
        const nonOverlappingLabels = [];
        const labels = [];

        traces.forEach(trace => {
            // Check if trace has text labels
            if (!trace.text || !Array.isArray(trace.text)) {
                labels.push([]); // Push empty array for traces without text
                return;
            }

            const traceLabels = [];
            for (let i = 0; i < trace.x.length; i++) {
                const x = trace.x[i];
                const y = trace.y[i];
                if (x >= xRange[0] && x <= xRange[1] && y >= yRange[0] && y <= yRange[1]) {
                    const overlap = nonOverlappingLabels.some((existingPoint) =>
                        isOverlappingScatter(existingPoint.x, existingPoint.y, x, y, rangeX, rangeY)
                    );

                    if (overlap) {
                        traceLabels.push("");
                    } else {
                        traceLabels.push(trace.text[i]); // Use original text, not hovertext
                        nonOverlappingLabels.push({ x, y });
                    }
                } else {
                    traceLabels.push("");
                }
            }
            labels.push(traceLabels);
        });

        return labels;
    }

    // Function to get current axis ranges from the plot
    const getCurrentRanges = () => {
        const plotElement = document.getElementById("graph");
        let xRange, yRange;
        
        if (plotElement && plotElement.layout && plotElement.layout.xaxis && plotElement.layout.yaxis) {
            // Try to get current ranges from plot layout
            xRange = plotElement.layout.xaxis.range || [
                Math.min(...data.graph.flatMap(trace => trace.x)),
                Math.max(...data.graph.flatMap(trace => trace.x))
            ];
            yRange = plotElement.layout.yaxis.range || [
                Math.min(...data.graph.flatMap(trace => trace.y)),
                Math.max(...data.graph.flatMap(trace => trace.y))
            ];
        } else {
            // Fallback to data ranges
            xRange = [
                Math.min(...data.graph.flatMap(trace => trace.x)),
                Math.max(...data.graph.flatMap(trace => trace.x))
            ];
            yRange = [
                Math.min(...data.graph.flatMap(trace => trace.y)),
                Math.max(...data.graph.flatMap(trace => trace.y))
            ];
        }

        return { xRange, yRange };
    };

    // Function to apply labels based on current state (works for all graph types)
    const applyLabels = useCallback(() => {
        // For clustered graphs with custom names, get the current traces from the plot
        const graphDiv = document.getElementById('graph');
        const currentTraces = (data.metric === "embeddings-cluster" && clusterNames && Object.keys(clusterNames).length > 0 && graphDiv && graphDiv.data)
            ? graphDiv.data
            : data.graph;

        // Apply labels based on current labelMode
        let updatedLabels;
        if (labelMode === "none") {
            updatedLabels = currentTraces.map(trace => {
                if (!trace.text || !Array.isArray(trace.text)) {
                    return []; // Return empty array if no text property
                }
                return trace.text.map(() => "");
            });
        } else if (labelMode === "all") {
            // Use the original text array which contains all labels
            updatedLabels = currentTraces.map(trace => {
                if (!trace.text || !Array.isArray(trace.text)) {
                    return []; // Return empty array if no text property
                }
                return [...trace.text];
            });
        } else if (labelMode === "dynamic" && supportsDynamicLabels()) {
            // Set dynamic labels with function (only for scatter plots)
            const { xRange, yRange } = getCurrentRanges();
            updatedLabels = chooseLabels(currentTraces, xRange, yRange);
        } else {
            // Fallback to all labels if dynamic is not supported
            updatedLabels = currentTraces.map(trace => {
                if (!trace.text || !Array.isArray(trace.text)) {
                    return []; // Return empty array if no text property
                }
                return [...trace.text];
            });
        }

        console.log("Updated labels:", updatedLabels.map((labels, i) => `Trace ${i}: ${labels.filter(l => l !== "").length} visible labels`));

        // Update the plot with new labels - only update traces that have text
        try {
            updatedLabels.forEach((labels, index) => {
                if (labels.length > 0) {
                    Plotly.restyle("graph", { text: [labels] }, index);
                }
            });
        } catch (error) {
            console.error("Error updating labels:", error);
        }
    }, [labelMode, data, clusterNames]);

    // Function to create initial graph data with appropriate labels
    const getInitialGraphData = useCallback(() => {
        console.log("getInitialGraphData called with clusterNames:", clusterNames);

        // For clustering metric, ALWAYS reorganize data by cluster to show legend
        if (data.metric === "embeddings-cluster" && data.graph.length > 0) {
            console.log("Creating clustered traces...");
            const trace = data.graph[0];
            if (trace.marker && trace.marker.color && Array.isArray(trace.marker.color)) {
                // Group points by cluster ID
                const clusterGroups = {};
                trace.marker.color.forEach((clusterId, idx) => {
                    if (!clusterGroups[clusterId]) {
                        clusterGroups[clusterId] = {
                            x: [],
                            y: [],
                            text: [],
                            hovertext: []
                        };
                    }
                    clusterGroups[clusterId].x.push(trace.x[idx]);
                    clusterGroups[clusterId].y.push(trace.y[idx]);
                    if (trace.text) clusterGroups[clusterId].text.push(trace.text[idx]);
                    if (trace.hovertext) clusterGroups[clusterId].hovertext.push(trace.hovertext[idx]);
                });

                // Create separate traces for each cluster
                const traces = Object.keys(clusterGroups).sort((a, b) => parseInt(a) - parseInt(b)).map(clusterId => {
                    const clusterData = clusterGroups[clusterId];
                    const clusterName = clusterNames[clusterId] || `Cluster ${clusterId}`;
                    console.log(`Cluster ${clusterId} -> ${clusterName}`);

                    // Handle text labels properly
                    let textLabels = [];
                    if (clusterData.text && clusterData.text.length > 0) {
                        if (labelMode === "none") {
                            textLabels = clusterData.text.map(() => "");
                        } else {
                            textLabels = clusterData.text;
                        }
                    }

                    return {
                        x: clusterData.x,
                        y: clusterData.y,
                        text: textLabels,
                        hovertext: clusterData.hovertext,
                        name: clusterName,
                        mode: 'markers+text',
                        type: 'scatter',
                        textposition: 'top center',
                        textfont: {
                            size: 10
                        },
                        marker: {
                            size: 12,
                            opacity: 0.7
                        }
                    };
                });

                console.log("Generated traces:", traces);
                return traces;
            }
        }

        // Default behavior for non-clustering or when no custom names
        return data.graph.map(trace => {
            let updatedTrace = { ...trace };

            // Check if trace has a text property that's iterable
            if (!trace.text || !Array.isArray(trace.text)) {
                // Return trace as-is if no text labels
                return updatedTrace;
            }

            let initialText;
            if (labelMode === "none") {
                initialText = trace.text.map(() => "");
            } else if (labelMode === "all") {
                initialText = [...trace.text];
            } else if (labelMode === "dynamic" && supportsDynamicLabels()) {
                // For dynamic on scatter plots, apply labels after the plot is created
                initialText = [...trace.text];
            } else {
                // Default to all labels
                initialText = [...trace.text];
            }

            return {
                ...updatedTrace,
                text: initialText,
                textangle: 90, 
                textfont: {
                    ...updatedTrace.textfont,
                    size: 10
                },
                marker: {
                    ...updatedTrace.marker,
                    size: 12
                }
            };
        });
    }, [data, clusterNames, labelMode]);

    // UseEffect: Reset label mode when metric changes
    useEffect(() => {
        if (data && data.metric) {
            const defaultMode = getDefaultLabelMode();
            setLabelMode(defaultMode);
        }
    }, [data?.metric]);

    // UseEffect: Generates graph object with zoom click event definition
    useEffect(() => {
        if (!data || !data.graph || data.graph.length === 0) {
            // Hide graph if no data
            setFoundData(false);
            setPlotInitialized(false);
        } else {
            // Generate graph if there is data
            setFoundData(true);

            // Update layout
            let layout_ = { ...layout };

            // Hide legend (but always show for clustering)
            if (data.metric === "embeddings-cluster") {
                layout_ = {
                    ...layout_,
                    showlegend: true
                };
            } else if (
                metricTypes.dotplot.includes(data.metric) ||
                metricTypes.multibar.includes(data.metric) ||
                metricTypes.bar.includes(data.metric) ||
                (metricTypes.scatter.includes(data.metric) && data.graph.length === 1) ||
                metricTypes.directedGraph.includes(data.metric)
            ) {
                layout_ = {
                    ...layout_,
                    showlegend: false
                };
            } else {
                layout_ = {
                    ...layout_,
                    showlegend: true
                };
            }

            // Treat x-axis as categorical (except for scatter, line, and directed graph)
            if (!(metricTypes.scatter.includes(data.metric) || metricTypes.line.includes(data.metric) || metricTypes.directedGraph.includes(data.metric))) {
                layout_ = {
                    ...layout_,
                    xaxis: {
                        ...layout_.xaxis,
                        type: "category"
                    }
                }

                // Set y-axis as categorical
                if (metricTypes.heatmap.includes(data.metric)) {
                    layout_ = {
                        ...layout_,
                        yaxis: {
                            ...layout_.yaxis,
                            type: "category"
                        }
                    }
                }
            }

            // For time-based embeddings similarity, x-axis shows whole numbers only
            if (data.metric === "embeddings-similarity-time") {
                layout_ = {
                    ...layout_,
                    xaxis: {
                        ...layout_.xaxis,
                        dtick: 1,
                        tick0: 0
                    }
                }
            }

            // Remove axis ticks
            if (metricTypes.directedGraph.includes(data.metric)) {
                layout_ = {
                    ...layout_,
                    xaxis: {
                        ...layout_.xaxis,
                        showticklabels: false
                    },
                    yaxis: {
                        ...layout_.yaxis,
                        showticklabels: false
                    }
                }
            }

            // Show annotations
            if (annotations) {
                layout_ = {
                    ...layout_,
                    annotations
                }
            } else if (Object.keys(layout_).includes("annotations")) {
                delete layout_.annotations;
            }

            setLayout({ ...layout_ });
        }
    }, [data, annotations]);

    // UseEffect to handle label mode changes
    useEffect(() => {
        if (foundData && plotInitialized) {
            // Small delay to ensure any plot updates are complete
            setTimeout(() => {
                applyLabels();
            }, 50);
        }
    }, [labelMode, plotInitialized]);

    // UseEffect to update cluster names in graph when clusterNames change
    useEffect(() => {
        console.log("Cluster names changed:", clusterNames);

        if (foundData && plotInitialized && data.metric === "embeddings-cluster" && clusterNames && Object.keys(clusterNames).length > 0) {
            console.log("Updating cluster names in legend and hover text...");

            // Get the graph element to check current state
            const graphDiv = document.getElementById('graph');
            if (!graphDiv || !graphDiv.data) {
                console.log("Graph not ready");
                return;
            }

            // Update names for all clusters that have been renamed
            Object.keys(clusterNames).forEach(clusterId => {
                const clusterIdNum = parseInt(clusterId);
                const newName = clusterNames[clusterId];
                const traceIndex = clusterIdNum - 1; 

                console.log(`Renaming Cluster ${clusterIdNum} (trace index ${traceIndex}) to "${newName}"`);

                // Update the trace name 
                Plotly.restyle('graph', { name: newName }, traceIndex);

                // Update hovertext to include new cluster name
                const trace = graphDiv.data[traceIndex];
                if (trace && trace.hovertext && Array.isArray(trace.hovertext)) {
                    const updatedHovertext = trace.hovertext.map(text => {
                        // Replace old cluster name in hovertext with new one
                        if (typeof text === 'string') {
                            return text.replace(/Cluster \d+/, newName);
                        }
                        return text;
                    });
                    Plotly.restyle('graph', { hovertext: [updatedHovertext] }, traceIndex);
                }
            });

            Plotly.relayout('graph', { showlegend: true });
            console.log("Cluster names updated successfully!");
        }
    }, [clusterNames, foundData, plotInitialized, data.metric]);

    // UseEffect to create and manage the plot
    useEffect(() => {
        if (foundData) {
            setPlotInitialized(false);
            
            const initialGraphData = getInitialGraphData();
            
            Plotly.newPlot('graph', initialGraphData, layout, { displayModeBar: "hover" }).then(() => {
                setPlotInitialized(true);
                
                // Apply initial labels after plot creation
                setTimeout(() => {
                    applyLabels();
                }, 100);
            });

            // Relayout event to handle dynamic labels on scatter plots
            graph.current.on("plotly_relayout", event => {
                console.log("🔄 Plotly relayout event triggered:", event);
                
                if (supportsDynamicLabels() && labelMode === "dynamic" && plotInitialized) {
                    setTimeout(() => {
                        applyLabels();
                    }, 50);
                }
            });

            // Click event for zoom page
            graph.current.on('plotly_click', (event) => { 
                setZoomLoading(true);
                const dataPoint = event.points[0];
                let idx;
                if (typeof dataPoint.pointIndex === "number") {
                    idx = dataPoint.pointIndex;
                } else {
                    idx = (dataPoint.pointIndex[0] + 1) * (dataPoint.pointIndex[1] + 1) - 1;
                }

                const params = JSON.parse(localStorage.getItem("graph-settings"));
                if (metricTypes.bar.includes(data.metric)) {
                    params.group_list = dataPoint.x;
                } else if (metricTypes.scatter.includes(data.metric)) {
                    params.word_list = [dataPoint.hovertext];
                } else if (metricTypes.heatmap.includes(data.metric)) {
                    params.group_list = [dataPoint.x, dataPoint.y];
                } else if (metricTypes.dotplot.includes(data.metric)) {
                    params.group_list = dataPoint.x;
                    params.word_list = [dataPoint.text, params.word_list[0]];
                } else if (metricTypes.multibar.includes(data.metric)) {
                    params.group_list = dataPoint.x;
                    params.word_list = [dataPoint.text];
                } else if (metricTypes.directedGraph.includes(data.metric)) {
                    if (dataPoint.data.hovertext.includes("<br>Weight: ")) {
                        // line
                        params.group_name = [params.from_col, params.to_col];
                        params.word_list = [];
                        let names = dataPoint.data.hovertext.split("<br>")[0];
                        params.group_list = names.split(" -> ");
                    } else {
                        // point
                        params.group_name = [params.to_col, params.from_col];
                        params.group_list = dataPoint.hovertext;
                        params.word_list = [];
                    }
                } else {
                    throw new Error("Graph type not supported")
                }

                getZoomIds(data.table_name, params).then(res => {
                    const tempData = {
                        x: dataPoint.x,
                        y: dataPoint.y,
                        name: res.name,
                        count: res.count,
                        dataset: data.table_name,
                        metric: data.metric,
                        word_list: params.word_list,
                        group_name: params.group_name,
                        group_list: params.group_list
                    };
                    localStorage.setItem('selected', JSON.stringify(tempData))
                    setZoomLoading(false);
                    navigate("/graph/zoom");
                });
            });
        }
    }, [foundData, layout, labelMode]); // Added labelMode here so plot recreates with correct initial labels

    return (
    <Box sx={{ width: '100%', pb: 3 }}>
        {/* Editable Title - Click to Edit */}
        {foundData && (
            <Box sx={{ textAlign: 'center', mb: 2, px: 2 }}>
                {isEditingTitle ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <TextField
                            autoFocus
                            value={customTitle || `${metricNames[data.metric]} For "${dataset.title}"`}
                            onChange={(e) => setCustomTitle(e.target.value)}
                            variant="standard"
                            sx={{
                                minWidth: '400px',
                                '& .MuiInput-root': { fontSize: '1.25rem', fontWeight: 500, textAlign: 'center' }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') setIsEditingTitle(false);
                                if (e.key === 'Escape') {
                                    setCustomTitle("");
                                    setIsEditingTitle(false);
                                }
                            }}
                        />
                        <IconButton size="small" onClick={() => setIsEditingTitle(false)} color="primary">
                            <Check fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => { setCustomTitle(""); setIsEditingTitle(false); }}>
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                        }}
                        onClick={() => setIsEditingTitle(true)}
                    >
                        <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 500 }}>
                            {customTitle || `${metricNames[data.metric]} For "${dataset.title}"`}
                        </Box>
                        <Edit sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    </Box>
                )}
            </Box>
        )}

        {/* Cluster Renaming Modal */}
        <Modal
            open={clusterModalOpen}
            onClose={() => setClusterModalOpen(false)}
            aria-labelledby="cluster-modal-title"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 600,
                maxWidth: '90%',
                bgcolor: 'background.paper',
                boxShadow: 24,
                borderRadius: 2,
                p: 4,
                maxHeight: '80vh',
                overflowY: 'auto'
            }}>
                <Typography id="cluster-modal-title" variant="h6" component="h2" sx={{ mb: 3 }}>
                    Rename Clusters
                </Typography>
                <Grid container spacing={2}>
                    {(() => {
                        // Get unique cluster IDs from the graph data
                        const clusterIds = new Set();
                        if (data?.graph?.[0]?.marker?.color) {
                            data.graph[0].marker.color.forEach(id => clusterIds.add(id));
                        }
                        // Also check text/hovertext for cluster information
                        data?.graph?.forEach(trace => {
                            if (trace.name && trace.name.toString().startsWith('Cluster')) {
                                const id = parseInt(trace.name.replace('Cluster ', ''));
                                if (!isNaN(id)) clusterIds.add(id);
                            }
                        });

                        return Array.from(clusterIds).sort((a, b) => a - b).map(clusterId => (
                            <Grid item xs={12} sm={6} key={clusterId}>
                                <TextField
                                    label={`Cluster ${clusterId}`}
                                    value={clusterNames[clusterId] || `Cluster ${clusterId}`}
                                    onChange={(e) => setClusterNames({
                                        ...clusterNames,
                                        [clusterId]: e.target.value
                                    })}
                                    size="small"
                                    fullWidth
                                />
                            </Grid>
                        ));
                    })()}
                </Grid>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={() => setClusterNames({})} variant="outlined">
                        Reset
                    </Button>
                    <Button onClick={() => setClusterModalOpen(false)} variant="contained">
                        Done
                    </Button>
                </Box>
            </Box>
        </Modal>

        {/* Graph container */}
        <Box className="d-flex" sx={{ px: 2, overflow: 'visible', position: 'relative' }}>
            {/* Rename Clusters Button - Only for clustering metrics, positioned above legend */}
            {data.metric === "embeddings-cluster" && (
                <Box sx={{
                    position: 'absolute',
                    right: 32,
                    top: 16,
                    zIndex: 1000
                }}>
                    <IconButton
                        size="small"
                        onClick={() => setClusterModalOpen(true)}
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                            }
                        }}
                        title="Rename clusters"
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <div id="graph"
                style={{ margin: "0 auto" }}
                ref={graph}
                hidden={!data.graph}
            ></div>
        </Box>

        {/* Dropdown for Label Mode - Only show if at least one trace has text labels */}
        {foundData && data.graph && data.graph.some(trace => trace.text && Array.isArray(trace.text)) && (
            <Box sx={{ 
                display: 'flex',
                justifyContent: 'center'
            }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="label-mode-select-label">Labels</InputLabel>
                    <Select
                        labelId="label-mode-select-label"
                        value={labelMode}
                        onChange={(e) => {
                            setLabelMode(e.target.value);
                        }}
                        label="Labels"
                    >
                        {getAvailableLabelModes().map(mode => (
                            <MenuItem key={mode.value} value={mode.value}>
                                {mode.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        )}
    </Box>
);
}